// ./src/components/features/Logbook/LogbookEditDrawer/sections/TankLevelSection.tsx

import React from "react";
import { Stack, Checkbox, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { FormData } from "../../types";

import { Car } from "@/utils/api/CarService";

import { CollapsibleSection } from "@/components/generic/CollapsibleSection";
import { TankLevelSlider } from "@/components/features";

// ============================================
// Types
// ============================================

export interface TankLevelSectionProps {
  formData: FormData;
  onTankLevelChange: (name: string, value: number) => void;
  onTankLevelUnknownChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  tankVolume: number;
  selectedCar: Car | null | undefined;
  /** Whether the section is collapsible (false for refuel — always visible) */
  collapsible?: boolean;
  /** Controlled expanded state (for collapsible mode) */
  expanded?: boolean;
  /** Toggle callback (for collapsible mode) */
  onToggle?: () => void;
}

// ============================================
// Component
// ============================================

const TankLevelSectionComponent: React.FC<TankLevelSectionProps> = ({
  formData,
  onTankLevelChange,
  onTankLevelUnknownChange,
  tankVolume,
  selectedCar,
  collapsible = true,
  expanded,
  onToggle,
}) => {
  const { t } = useTranslation(["refuels", "logbook"]);

  const refuelVolume =
    formData.expenseType === 1 ? formData.refuelVolume || 0 : 0;

  const content = (
    <Stack gap="sm">
      <Checkbox
        label={t("refuels:form.fields.tankLevel.unknown", {
          defaultValue: "I don't know the tank level",
        })}
        checked={formData.tankLevelUnknown}
        onChange={onTankLevelUnknownChange}
        size="md"
      />

      {!formData.tankLevelUnknown && (
        <>
          <TankLevelSlider
            name="tankLevelPercent"
            value={formData.tankLevelPercent}
            onChange={onTankLevelChange}
            tankVolume={tankVolume}
            refuelVolume={refuelVolume}
            showCalculations={tankVolume > 0}
            size="md"
          />

          {tankVolume === 0 && selectedCar && (
            <Text size="xs" c="dimmed">
              {t("refuels:form.fields.tankLevel.noTankVolume", {
                defaultValue:
                  "Set tank volume in vehicle settings to enable fuel calculations",
              })}
            </Text>
          )}
        </>
      )}
    </Stack>
  );

  if (!collapsible) {
    // For refuels: render tank level directly with a divider label
    return (
      <CollapsibleSection
        title={t("refuels:form.sections.tank")}
        alwaysExpanded
        withDivider
        dividerLabel={t("refuels:form.sections.tank")}
      >
        {content}
      </CollapsibleSection>
    );
  }

  // For expenses/checkpoints: collapsible optional section
  return (
    <CollapsibleSection
      title={t("logbook:form.sections.tankLevel", { defaultValue: "Tank Level" })}
      expanded={expanded}
      onToggle={onToggle!}
      optional
      withDivider
    >
      {content}
    </CollapsibleSection>
  );
};

export const TankLevelSection = React.memo(TankLevelSectionComponent);